import React, {useEffect} from 'react';
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import {useSearchParams} from "react-router-dom";

import './list_page.scss';
import DownloadCSV from "../../components/DownloadCSV";
import SearchResult from "../../components/SearchResult";
import {compileItemForExport, getKeysForExport, prepareOptions} from "./list_page_utils";
import Tips from "./Tips";
import Filters from '../../components/Filters'
import Paginator from "../../components/Paginator";
import Sort from "../../components/Sort";
import QuickSearch from "../../components/QuickSearch";
import {ReloadListAction} from "./actions";
import {iCan} from "../../../utils/auth";
import {ErrorMessageAction} from "../../components/CustomToast/actions";
import {catchQueryFilters} from "../../components/Filters/filters_utils";

const ListPage = function (
    {
        children = null,
        custom_export = null,
        filter_columns = [],
        sort_columns = [],
        quick_search_placeholder = 'Type a name',
        page_classes = '',
        report_title = 'report',
        show_quick_search = true,
        show_paginator = true,
        tips = [],
        query_divider = '~',
        action,
        create_button = <></>,
        entity = 'generic',
        permissions = [],
        extra = {},
        default_order_by = 'id',
        default_order_direction = 'asc'
    }
) {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = catchQueryFilters();
    const q = searchParams.has('q') ? searchParams.get('q') : '';
    const items = useSelector(state => state[entity]);
    const current = useSelector(state => state[entity + '_current']);
    const limit = useSelector(state => state[entity + '_limit']);
    const order_by = searchParams.has('order_by') && sort_columns.map(column => column.key).includes(default_order_by) ? searchParams.get('order_by') : default_order_by;
    const order_direction = searchParams.has('order_direction') ? searchParams.get('order_direction') : default_order_direction;
    const reload = useSelector(state => state.hasOwnProperty('list_reload') ? state.list_reload : false);
    const me = useSelector(state => state.me);

    useEffect(() => {
        if (iCan(me, permissions)) {
            dispatch(action(prepareOptions({
                q,
                current,
                limit,
                order_by,
                order_direction,
                filters,
                extra
            })));
        } else {
            dispatch(ErrorMessageAction('You do not have enough permissions to access this area', true));
        }
    }, [current, limit, order_by, order_direction]);
    useEffect(() => {
        if (reload && iCan(me, permissions)) {
            dispatch(action(prepareOptions({
                q,
                current,
                limit,
                order_by,
                order_direction,
                filters,
                extra
            })));
            dispatch(ReloadListAction(false, entity));
        } else if (reload) {
            dispatch(ReloadListAction(false, entity));
            dispatch(ErrorMessageAction('You do not have enough permissions to access this area', true));
        }
    }, [reload]);

    return (
        <div className={page_classes} id='list-page'>
            <div className="top-panel">
                <QuickSearch
                    action={() => dispatch(ReloadListAction(true, entity))}
                    show_quick_search={show_quick_search}
                    quick_search_placeholder={quick_search_placeholder}
                />
                <Sort
                    columns={sort_columns}
                    entity={entity}
                    default_order_by={default_order_by}
                    default_order_direction={default_order_direction}
                />
                {create_button}
            </div>
            <Tips tips={tips}/>
            <Filters
                filter_columns={filter_columns}
                query_divider={query_divider}
            />
            <div className='search-results'>
                <SearchResult entity={entity}/>
                {custom_export ? custom_export : (
                    <button
                        onClick={event => {
                            event.preventDefault();
                            const keys = getKeysForExport(items);
                            let rows = [];
                            rows.push(Object.values(keys));
                            items.map(item => rows.push(compileItemForExport(item, Object.keys(keys))));
                            new DownloadCSV().execute(report_title, rows);
                        }}
                        disabled={items.length <= 0}
                    >Export to CSV</button>
                )}
            </div>
            {children}
            <Paginator entity={entity} show={show_paginator}/>
        </div>
    );
}

ListPage.propTypes = {
    action: PropTypes.func.isRequired,
    filter_columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        expressions: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        multiple: PropTypes.bool,
        component: PropTypes.func
    })),
    sort_columns: PropTypes.array,
    quick_search_placeholder: PropTypes.string,
    page_classes: PropTypes.string,
    report_title: PropTypes.string,
    show_quick_search: PropTypes.bool,
    show_paginator: PropTypes.bool,
    tips: PropTypes.arrayOf(PropTypes.string),
    custom_export: PropTypes.object,
    create_button: PropTypes.object,
    entity: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
    default_order_by: PropTypes.string,
    default_order_direction: PropTypes.oneOf(['asc', 'desc'])
}

export default ListPage;
